import { useCallback, useState } from 'react';
import axios from 'axios';
import { useOrgStore } from '../store/useOrgStore';
import type { AuditBundle } from '../types/org.types';

export type AuditStepKey =
  | 'overview'
  | 'objects'
  | 'flows'
  | 'users'
  | 'permissions'
  | 'techdebt'
  | 'foundations'
  | 'email'
  | 'datacloud'
  | 'activity'
  | 'licenses'
  | 'apps'
  | 'storage'
  | 'datahygiene'
  | 'healthscore';

const STEPS: { key: AuditStepKey; path: string }[] = [
  { key: 'overview', path: '/api/org/overview' },
  { key: 'objects', path: '/api/org/objects' },
  { key: 'flows', path: '/api/org/flows' },
  { key: 'users', path: '/api/org/users' },
  { key: 'permissions', path: '/api/org/permissions' },
  { key: 'techdebt', path: '/api/org/techdebt' },
  { key: 'foundations', path: '/api/org/foundations' },
  { key: 'email', path: '/api/org/email' },
  { key: 'datacloud', path: '/api/org/datacloud' },
  { key: 'activity', path: '/api/org/activity' },
  { key: 'licenses', path: '/api/org/licenses' },
  { key: 'apps', path: '/api/org/apps' },
  { key: 'storage', path: '/api/org/storage' },
  { key: 'datahygiene', path: '/api/org/datahygiene' },
  { key: 'healthscore', path: '/api/org/healthscore' },
];

export type StepStatus = 'pending' | 'active' | 'done' | 'error';

export type AuditState = {
  steps: Record<AuditStepKey, StepStatus>;
  errors: Partial<Record<AuditStepKey, string>>;
  running: boolean;
};

const INITIAL: AuditState = {
  steps: {
    overview: 'pending',
    objects: 'pending',
    flows: 'pending',
    users: 'pending',
    permissions: 'pending',
    techdebt: 'pending',
    foundations: 'pending',
    email: 'pending',
    datacloud: 'pending',
    activity: 'pending',
    licenses: 'pending',
    apps: 'pending',
    storage: 'pending',
    datahygiene: 'pending',
    healthscore: 'pending',
  },
  errors: {},
  running: false,
};

export function useAuditData() {
  const setAudit = useOrgStore((s) => s.setAudit);
  const [state, setState] = useState<AuditState>(INITIAL);

  const run = useCallback(async () => {
    setState({ ...INITIAL, running: true, steps: { ...INITIAL.steps } });

    // Mark all as active and dispatch in parallel; resolve each step independently.
    setState((s) => ({
      ...s,
      steps: Object.fromEntries(STEPS.map((st) => [st.key, 'active'])) as AuditState['steps'],
    }));

    const results: AuditBundle = {};

    await Promise.all(
      STEPS.map(async ({ key, path }) => {
        try {
          const res = await axios.get(path, { withCredentials: true });
          (results as Record<AuditStepKey, unknown>)[key] = res.data;
          setState((s) => ({ ...s, steps: { ...s.steps, [key]: 'done' } }));
        } catch (err: unknown) {
          const msg = axios.isAxiosError(err)
            ? (err.response?.data?.error ?? err.message)
            : (err as Error).message;
          setState((s) => ({
            ...s,
            steps: { ...s.steps, [key]: 'error' },
            errors: { ...s.errors, [key]: msg },
          }));
        }
      }),
    );

    setAudit(results);
    setState((s) => ({ ...s, running: false }));

    const failed = STEPS.filter(
      (st) => (results as Record<string, unknown>)[st.key] === undefined,
    );
    return { ok: failed.length === 0, failed: failed.map((s) => s.key) };
  }, [setAudit]);

  return { run, state };
}
